import React, { useState, useEffect } from "react";
import { Card, CardBody, Col, Button, Spinner, Input, Row, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import DataTable from "react-data-table-component";
import { CustomerTitle, ViewCustomerTitle } from "../../../Utils/Constants";
import Breadcrumbs from "../../../CommonElements/Breadcrumbs";
import axios from "axios";
import useCurrency, { baseUrl, mediaUrl } from "../../../Utils/FileUpload";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { Video, Phone, Mail, MapPin, Globe, MessageSquare, Calendar, CreditCard, Star, Clock, ShoppingBag, DollarSign, Activity, Eye } from "react-feather";
import { useDebouncedCallback } from "use-debounce";
import { useAuth } from "../../../Context/AuthContext";

interface Customer {
  id: string;
  name?: string;
  email?: string;
  mobile: number;
  image?: string;
  location?: string;
  latitude?: string;
  longitude?: string;
  country?: string;
  preferred_languages?: string[] | string;
  is_active: number;
  created_at?: string;
  amount: string;
  total_package_amount: string;
  currency: string;
  amount_spent: string;
  amount_without_currency: number;
  total_package_amount_without_currency: number;
  amount_spent_without_currency: number;
  wallet_credit?: number;
  wallet_minutes?: number;
  audio_amount?: number;
  audio_credit?: number;
  audio_minutes?: number;
  video_amount?: number;
  video_credit?: number;
  video_minutes?: number;
}

export default function Customer() {
  const [filterText, setFilterText] = useState("");
  const [filteredItems, setFilteredItems] = useState<Customer[]>([]);
  const token = localStorage.getItem("token");
  const navigate = useNavigate();
  const { currency } = useCurrency();
  const { hasPermission } = useAuth();
  const [totalRows, setTotalRows] = useState<number>(0);
  const [perPage, setPerPage] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [loading, setLoading] = useState(true);
  const [disabledEnable, setDisabledEnable] = useState<string | null>(null);
  const [disableAllEnable, setDisableAllEnable] = useState(false);
  const [refresh, setRefresh] = useState(0);

  // Detail Modal State
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  // Country counts state
  const [countryCounts, setCountryCounts] = useState({
    india: 0,
    uae: 0,
    ksa: 0,
    total: 0,
  });

  const fetchCountryCounts = async () => {
    try {
      const response = await axios.get(`${baseUrl}customer_detail/country_counts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data) {
        const countsData = response.data.data || response.data;
        setCountryCounts({
          india: countsData.india || 0,
          uae: countsData.uae || 0,
          ksa: countsData.ksa || 0,
          total: countsData.total || 0,
        });
      }
    } catch (error) {
      console.error("Error fetching country counts:", error);
    }
  };

  // Sorting State
  const [sortField, setSortField] = useState<string>("created_at");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

  const fetchCustomerData = async (
    page: number,
    size: number,
    search: string = "",
    field: string = sortField,
    direction: "asc" | "desc" = sortDirection
  ) => {
    setLoading(true);

    try {
      const response = await axios.get(`${baseUrl}customer_detail/get_all_paginated`, {
        params: {
          page: page,
          per_page: size,
          search: search,
          sort_field: field,
          sort_direction: direction,
        },
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data && response.data.data) {
        setFilteredItems(response.data.data);
        setTotalRows(response.data.total || 0);
      }
    } catch (error) {
      console.error("Error fetching customer data:", error);
      toast.error("Failed to load customer data.");
    } finally {
      setLoading(false);
    }
  };

  const debouncedFetch = useDebouncedCallback((searchQuery: string) => {
    setCurrentPage(1);
    fetchCustomerData(1, perPage, searchQuery, sortField, sortDirection);
  }, 400);

  useEffect(() => {
    fetchCountryCounts();
    fetchCustomerData(currentPage, perPage, filterText, sortField, sortDirection);
  }, [refresh]);

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFilterText(value);
    debouncedFetch(value);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchCustomerData(page, perPage, filterText, sortField, sortDirection);
  };

  const handlePerRowsChange = async (newPerPage: number, page: number) => {
    setPerPage(newPerPage);
    setCurrentPage(page);
    fetchCustomerData(page, newPerPage, filterText, sortField, sortDirection);
  };

  const handleSort = (column: any) => {
    const field = column.sortField || "created_at";
    const newDirection: "asc" | "desc" =
      field === sortField
        ? sortDirection === "asc"
          ? "desc"
          : "asc"
        : "asc";

    console.log("🔍 [Frontend Sort Clicked]", {
      sortField: field,
      prevDirection: sortDirection,
      newDirection: newDirection,
    });

    setSortField(field);
    setSortDirection(newDirection);
    fetchCustomerData(1, perPage, filterText, field, newDirection);
  };

  const handlecustomerhistory = (id: string) => {
    navigate("/call_history", { state: { id: id, customer_id: id } });
  };

  const handlecustomervideohistory = (id: string) => {
    navigate("/video_call_history", { state: { id: id, customer_id: id } });
  };

  const handlePackageHistory = (id: string) => {
    navigate("/package_bought", { state: { id: id, customer_id: id } });
  };

  const handleViewDetails = (customer: Customer) => {
    setSelectedCustomer(customer);
    setDetailModalOpen(true);
  };

  const toggleEnable = (id: string, currentStatus: number) => {
    setDisabledEnable(id);
    setDisableAllEnable(true);

    const confirmToggle = () => {
      toast.warn(
        <div>
          <div>
            Are you sure you want to{" "}
            <strong>{currentStatus === 1 ? "disable" : "enable"}</strong> this
            customer?
          </div>
          <div className="d-flex justify-content-end mt-2">
            <Button
              color={currentStatus === 1 ? "danger" : "success"}
              onClick={() => confirmToggleEnable(id, currentStatus)}
            >
              Yes
            </Button>
            <Button
              color="secondary"
              onClick={() => {
                setDisabledEnable(null);
                setDisableAllEnable(false);
                toast.dismiss();
              }}
              className="ms-2"
            >
              Cancel
            </Button>
          </div>
        </div>,
        {
          position: "top-right",
          autoClose: false,
          closeOnClick: false,
          closeButton: false,
          draggable: false,
          onClose: () => {
            setDisabledEnable(null);
            setDisableAllEnable(false);
          },
        }
      );
    };

    const confirmToggleEnable = async (id: string, currentStatus: number) => {
      setDisabledEnable(null);
      setDisableAllEnable(false);
      toast.dismiss();

      try {
        const newStatus = currentStatus === 1 ? 0 : 1;

        await axios.post(
          `${baseUrl}customer_detail/toggle_active`,
          { id, is_active: newStatus },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        toast.success(
          `Customer ${newStatus === 1 ? "enabled" : "disabled"} successfully!`,
          {
            position: "top-right",
            autoClose: 1000,
            closeOnClick: true,
            onClose: () => {
              setRefresh((prev) => prev + 1);
            },
          }
        );
      } catch (error) {
        console.error("Error toggling customer:", error);
        toast.error("Error updating status", {
          position: "top-right",
          autoClose: 1000,
        });
      }
    };

    confirmToggle();
  };

  const formatMobile = (mobile?: string | number) => {
    if (!mobile) return "N/A";
    const str = String(mobile).trim();
    const cleanStr = str.replace(/^\++/, "");
    return `+${cleanStr}`;
  };

  const renderHeader = (title: string, field: string) => {
    const isSorted = sortField === field;
    return (
      <div
        className="d-flex align-items-center gap-1 text-wrap"
        style={{ whiteSpace: "normal", wordBreak: "break-word", lineHeight: "1.2" }}
      >
        <span>{title}</span>
        {isSorted && (
          <span style={{ color: "#308e87", fontSize: "12px", fontWeight: "bold", marginLeft: "2px" }}>
            {sortDirection === "asc" ? "▲" : "▼"}
          </span>
        )}
      </div>
    );
  };

  const customerTableColumns = [
    {
      name: <div>S.No</div>,
      cell: (row: Customer, index?: number) => (
        <span>{(currentPage - 1) * perPage + (index ?? 0) + 1}</span>
      ),
      width: "6%",
    },
    {
      name: renderHeader("Mobile", "mobile"),
      selector: (row: Customer) => row.mobile,
      sortable: true,
      sortField: "mobile",
      cell: (row: Customer) => <span>{formatMobile(row.mobile)}</span>,
      width: "14%",
    },
    {
      name: renderHeader("Name", "name"),
      selector: (row: Customer) => row.name || "N/A",
      sortable: true,
      sortField: "name",
      width: "12%",
    },
    {
      name: renderHeader(`Wallet Amount (${currency || "₹"})`, "amount"),
      selector: (row: Customer) => row.amount,
      sortable: true,
      sortField: "amount",
      width: "12%",
    },
    {
      name: renderHeader(`Package Amount Bought (${currency || "₹"})`, "total_package_amount"),
      selector: (row: Customer) => row.total_package_amount,
      sortable: true,
      sortField: "total_package_amount",
      width: "16%",
    },
    {
      name: renderHeader(`Amount Spent (${currency || "₹"})`, "amount_spent"),
      selector: (row: Customer) => row.amount_spent,
      sortable: true,
      sortField: "amount_spent",
      width: "12%",
    },
    {
      name: renderHeader("Status", "is_active"),
      sortable: true,
      sortField: "is_active",
      cell: (row: Customer) => (
        <span
          className={`badge rounded-pill ${row.is_active === 1 ? "bg-success" : "bg-danger"}`}
        >
          {row.is_active === 1 ? "Enable" : "Disable"}
        </span>
      ),
      width: "10%",
    },
    {
      name: <div>Actions</div>,
      cell: (row: Customer) => (
        <div className="d-flex align-items-center gap-1">
          {hasPermission("customer.view") && (
            <Button
              color="info"
              size="sm"
              onClick={() => handleViewDetails(row)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "4px 8px",
              }}
              title="View Customer Details"
            >
              <Eye size={14} />
            </Button>
          )}
          {hasPermission("customer.enable_disable") && (
            <Button
              color={row.is_active ? "success" : "secondary"}
              size="sm"
              onClick={() => toggleEnable(row.id, row.is_active)}
              disabled={disabledEnable === row.id || disableAllEnable}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "4px 8px",
              }}
              title={row.is_active ? "Disable" : "Enable"}
            >
              <i
                className={`fa ${row.is_active ? "fa-check-circle" : "fa-ban"}`}
              />
            </Button>
          )}
          {hasPermission("customer.call_history") && (
            <Button
              color="info"
              size="sm"
              onClick={() => handlecustomerhistory(row.id)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "4px 8px",
              }}
              title="Call History"
            >
              <i className="fa fa-phone" />
            </Button>
          )}
          {hasPermission("customer.package_bought") && (
            <Button
              color="secondary"
              size="sm"
              onClick={() => handlePackageHistory(row.id)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "4px 8px",
              }}
              title="Package Bought"
            >
              <i className="fa fa-cube" />
            </Button>
          )}
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      width: "18%",
    },
  ];

  const customStyles = {
    headCells: {
      style: {
        fontSize: "16px",
        fontWeight: "600",
        color: "#308e87",
        backgroundColor: "#f8f9fa",
        borderBottom: "2px solid #308e87",
        "& span[class*='SortIcon']": {
          display: "none !important",
        },
        "& .rdt_TableCol_SortIcon": {
          display: "none !important",
        },
        "& svg": {
          display: "none !important",
        },
      },
    },
    rows: {
      style: {
        borderBottom: "1px solid #dee2e6",
      },
    },
    table: {
      style: {
        border: "1px solid #dee2e6",
        borderRadius: "8px",
      },
    },
  };

  return (
    <>
      <Breadcrumbs
        pageTitle={ViewCustomerTitle}
        parent={CustomerTitle}
        title={ViewCustomerTitle}
      />
      <div className="container-fluid">
        <Row className="mb-4">
          <Col md={3}>
            <Card className="shadow-sm border-0 bg-light-primary">
              <CardBody className="p-3 d-flex align-items-center justify-content-between">
                <div style={{ fontSize: "2rem" }}>🇮🇳</div>
                <div className="text-end">
                  <h4 className="mb-0 text-primary">{countryCounts.india}</h4>
                  <span className="text-muted" style={{ fontSize: "0.85rem" }}>
                    India Customers
                  </span>
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="shadow-sm border-0 bg-light-success">
              <CardBody className="p-3 d-flex align-items-center justify-content-between">
                <div style={{ fontSize: "2rem" }}>🇦🇪</div>
                <div className="text-end">
                  <h4 className="mb-0 text-success">{countryCounts.uae}</h4>
                  <span className="text-muted" style={{ fontSize: "0.85rem" }}>
                    UAE Customers
                  </span>
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="shadow-sm border-0 bg-light-warning">
              <CardBody className="p-3 d-flex align-items-center justify-content-between">
                <div style={{ fontSize: "2rem" }}>🇸🇦</div>
                <div className="text-end">
                  <h4 className="mb-0 text-warning">{countryCounts.ksa}</h4>
                  <span className="text-muted" style={{ fontSize: "0.85rem" }}>
                    KSA Customers
                  </span>
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="shadow-sm border-0 bg-light-info">
              <CardBody className="p-3 d-flex align-items-center justify-content-between">
                <div style={{ fontSize: "2rem" }}>🌍</div>
                <div className="text-end">
                  <h4 className="mb-0 text-info">{countryCounts.total}</h4>
                  <span className="text-muted" style={{ fontSize: "0.85rem" }}>
                    Total Customers
                  </span>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {hasPermission("customer.view") && (
          <Col xl={12} className="mb-lg-3">
            <Card>
              <CardBody>
                <Row className="align-items-center justify-content-end">
                  <Col xl={4}>
                    <div className="mb-3">
                      <Input
                        type="text"
                        placeholder="Search Customer..."
                        value={filterText}
                        onChange={handleSearchInputChange}
                      />
                    </div>
                  </Col>
                </Row>

                <div className="table-responsive custom-scrollbar">
                  {loading ? (
                    <div className="text-center p-5">
                      <Spinner color="primary" />
                    </div>
                  ) : (
                    <DataTable
                      data={filteredItems}
                      columns={customerTableColumns}
                      highlightOnHover
                      striped
                      customStyles={customStyles}
                      pagination
                      paginationServer
                      paginationPerPage={perPage}
                      paginationTotalRows={totalRows}
                      onChangePage={handlePageChange}
                      onChangeRowsPerPage={handlePerRowsChange}
                      paginationDefaultPage={currentPage}
                      sortServer
                      onSort={handleSort}
                      sortIcon={<span style={{ display: "none" }} />}
                      responsive
                    />
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>
        )}
      </div>

      {/* Customer Detail Modal */}
      <Modal isOpen={detailModalOpen} toggle={() => setDetailModalOpen(false)} size="lg" centered>
        <ModalHeader toggle={() => setDetailModalOpen(false)} className="border-bottom-0 pb-0">
          <span className="fw-bold text-dark fs-5">Customer Profile & Details</span>
        </ModalHeader>
        <ModalBody className="px-4 pb-4">
          {selectedCustomer && (
            <div>
              {/* Hero Card */}
              <div
                className="text-center mb-4 p-4 rounded-4 position-relative overflow-hidden"
                style={{
                  background: "linear-gradient(135deg, #1b9aaa 0%, #16827c 100%)",
                  color: "#ffffff",
                  boxShadow: "0 8px 24px rgba(22, 130, 124, 0.25)",
                }}
              >
                <div className="d-inline-block position-relative mb-2">
                  {selectedCustomer.image ? (
                    <img
                      src={`${mediaUrl}/${selectedCustomer.image}`}
                      alt={selectedCustomer.name || "Customer"}
                      width="90"
                      height="90"
                      style={{
                        objectFit: "cover",
                        borderRadius: "50%",
                        border: "3px solid #ffffff",
                        boxShadow: "0 6px 16px rgba(0,0,0,0.15)",
                      }}
                    />
                  ) : (
                    <div
                      className="d-flex align-items-center justify-content-center bg-white text-dark rounded-circle mx-auto fw-bold fs-3"
                      style={{ width: "90px", height: "90px", border: "3px solid #ffffff" }}
                    >
                      {(selectedCustomer.name ? selectedCustomer.name.charAt(0) : "C").toUpperCase()}
                    </div>
                  )}
                </div>
                <h3 className="fw-bold text-white mb-1">{selectedCustomer.name || "Customer #" + selectedCustomer.id}</h3>
                <p className="text-white-50 small mb-2">{formatMobile(selectedCustomer.mobile)}</p>
                <div className="d-flex align-items-center justify-content-center gap-2">
                  <span
                    className="badge rounded-pill px-3 py-2 fw-semibold"
                    style={{
                      backgroundColor: selectedCustomer.is_active === 1 ? "rgba(255,255,255,0.25)" : "rgba(239, 68, 68, 0.8)",
                      backdropFilter: "blur(4px)",
                      color: "#ffffff",
                      fontSize: "12px",
                    }}
                  >
                    {selectedCustomer.is_active === 1 ? "● Active Customer" : "● Disabled"}
                  </span>
                </div>
              </div>

              {/* Personal Details Grid */}
              <h6 className="fw-bold text-dark mb-3 text-uppercase tracking-wider fs-7">
                <i className="fa fa-user me-2 text-info" /> Profile Information
              </h6>
              <Row className="g-3 mb-4">
                <Col md={6}>
                  <div className="p-3 border rounded-3 bg-white shadow-sm h-100 d-flex align-items-center">
                    <div
                      className="rounded-3 d-flex align-items-center justify-content-center me-3"
                      style={{ width: "42px", height: "42px", backgroundColor: "#e0f2fe", color: "#0284c7" }}
                    >
                      <Phone size={20} />
                    </div>
                    <div>
                      <span className="text-muted small d-block fw-semibold">Mobile Number</span>
                      <strong className="text-dark">{formatMobile(selectedCustomer.mobile)}</strong>
                    </div>
                  </div>
                </Col>

                <Col md={6}>
                  <div className="p-3 border rounded-3 bg-white shadow-sm h-100 d-flex align-items-center">
                    <div
                      className="rounded-3 d-flex align-items-center justify-content-center me-3"
                      style={{ width: "42px", height: "42px", backgroundColor: "#f3e8ff", color: "#7367f0" }}
                    >
                      <Mail size={20} />
                    </div>
                    <div>
                      <span className="text-muted small d-block fw-semibold">Email Address</span>
                      <strong className="text-dark">{selectedCustomer.email || "N/A"}</strong>
                    </div>
                  </div>
                </Col>

                <Col md={6}>
                  <div className="p-3 border rounded-3 bg-white shadow-sm h-100 d-flex align-items-center">
                    <div
                      className="rounded-3 d-flex align-items-center justify-content-center me-3"
                      style={{ width: "42px", height: "42px", backgroundColor: "#ecfdf5", color: "#10b981" }}
                    >
                      <MapPin size={20} />
                    </div>
                    <div>
                      <span className="text-muted small d-block fw-semibold">Location / Address</span>
                      <strong className="text-dark">{selectedCustomer.location || "N/A"}</strong>
                    </div>
                  </div>
                </Col>

                <Col md={6}>
                  <div className="p-3 border rounded-3 bg-white shadow-sm h-100 d-flex align-items-center">
                    <div
                      className="rounded-3 d-flex align-items-center justify-content-center me-3"
                      style={{ width: "42px", height: "42px", backgroundColor: "#eff6ff", color: "#2563eb" }}
                    >
                      <Globe size={20} />
                    </div>
                    <div>
                      <span className="text-muted small d-block fw-semibold">Country</span>
                      <strong className="text-dark">{selectedCustomer.country || "N/A"}</strong>
                    </div>
                  </div>
                </Col>

                <Col md={6}>
                  <div className="p-3 border rounded-3 bg-white shadow-sm h-100 d-flex align-items-center">
                    <div
                      className="rounded-3 d-flex align-items-center justify-content-center me-3"
                      style={{ width: "42px", height: "42px", backgroundColor: "#fff7ed", color: "#ea580c" }}
                    >
                      <MessageSquare size={20} />
                    </div>
                    <div>
                      <span className="text-muted small d-block fw-semibold">Preferred Languages</span>
                      <strong className="text-dark">
                        {Array.isArray(selectedCustomer.preferred_languages)
                          ? selectedCustomer.preferred_languages.join(", ")
                          : typeof selectedCustomer.preferred_languages === "string"
                            ? selectedCustomer.preferred_languages
                            : "Not Selected"}
                      </strong>
                    </div>
                  </div>
                </Col>

                <Col md={6}>
                  <div className="p-3 border rounded-3 bg-white shadow-sm h-100 d-flex align-items-center">
                    <div
                      className="rounded-3 d-flex align-items-center justify-content-center me-3"
                      style={{ width: "42px", height: "42px", backgroundColor: "#fef3c7", color: "#d97706" }}
                    >
                      <Calendar size={20} />
                    </div>
                    <div>
                      <span className="text-muted small d-block fw-semibold">Registration Date</span>
                      <strong className="text-dark">{selectedCustomer.created_at || "N/A"}</strong>
                    </div>
                  </div>
                </Col>
              </Row>

              {/* Financial & Wallet Section */}
              <h6 className="fw-bold text-dark mb-3 text-uppercase tracking-wider fs-7">
                <i className="fa fa-wallet me-2 text-success" /> Wallet & Financial Details
              </h6>
              <Row className="g-3">
                <Col md={4}>
                  <div className="p-3 border rounded-3 bg-white shadow-sm h-100 d-flex align-items-center">
                    <div
                      className="rounded-3 d-flex align-items-center justify-content-center me-3"
                      style={{ width: "42px", height: "42px", backgroundColor: "#e6f8f6", color: "#16827c" }}
                    >
                      <CreditCard size={20} />
                    </div>
                    <div>
                      <span className="text-muted small d-block fw-semibold">Wallet Balance</span>
                      <strong className="text-primary fs-6">{selectedCustomer.amount}</strong>
                    </div>
                  </div>
                </Col>

                <Col md={4}>
                  <div className="p-3 border rounded-3 bg-white shadow-sm h-100 d-flex align-items-center">
                    <div
                      className="rounded-3 d-flex align-items-center justify-content-center me-3"
                      style={{ width: "42px", height: "42px", backgroundColor: "#f3e8ff", color: "#7367f0" }}
                    >
                      <Star size={20} />
                    </div>
                    <div>
                      <span className="text-muted small d-block fw-semibold">Wallet Credits</span>
                      <strong className="text-dark fs-6">{selectedCustomer.wallet_credit ?? 0} pts</strong>
                    </div>
                  </div>
                </Col>

                <Col md={4}>
                  <div className="p-3 border rounded-3 bg-white shadow-sm h-100 d-flex align-items-center">
                    <div
                      className="rounded-3 d-flex align-items-center justify-content-center me-3"
                      style={{ width: "42px", height: "42px", backgroundColor: "#e0f2fe", color: "#0284c7" }}
                    >
                      <Clock size={20} />
                    </div>
                    <div>
                      <span className="text-muted small d-block fw-semibold">Talk Time Minutes</span>
                      <strong className="text-dark fs-6">{selectedCustomer.wallet_minutes ?? 0} Mins</strong>
                    </div>
                  </div>
                </Col>

                <Col md={4}>
                  <div className="p-3 border rounded-3 bg-white shadow-sm h-100 d-flex align-items-center">
                    <div
                      className="rounded-3 d-flex align-items-center justify-content-center me-3"
                      style={{ width: "42px", height: "42px", backgroundColor: "#fff7ed", color: "#ea580c" }}
                    >
                      <ShoppingBag size={20} />
                    </div>
                    <div>
                      <span className="text-muted small d-block fw-semibold">Total Packages Value</span>
                      <strong className="text-dark fs-6">{selectedCustomer.total_package_amount}</strong>
                    </div>
                  </div>
                </Col>

                <Col md={4}>
                  <div className="p-3 border rounded-3 bg-white shadow-sm h-100 d-flex align-items-center">
                    <div
                      className="rounded-3 d-flex align-items-center justify-content-center me-3"
                      style={{ width: "42px", height: "42px", backgroundColor: "#ecfdf5", color: "#10b981" }}
                    >
                      <DollarSign size={20} />
                    </div>
                    <div>
                      <span className="text-muted small d-block fw-semibold">Total Amount Spent</span>
                      <strong className="text-success fs-6">{selectedCustomer.amount_spent}</strong>
                    </div>
                  </div>
                </Col>

                <Col md={4}>
                  <div className="p-3 border rounded-3 bg-white shadow-sm h-100 d-flex align-items-center">
                    <div
                      className="rounded-3 d-flex align-items-center justify-content-center me-3"
                      style={{ width: "42px", height: "42px", backgroundColor: "#fef3c7", color: "#d97706" }}
                    >
                      <Activity size={20} />
                    </div>
                    <div>
                      <span className="text-muted small d-block fw-semibold">Audio / Video Usage</span>
                      <strong className="text-dark small d-block">
                        🎧 {selectedCustomer.audio_minutes ?? 0}m | 📹 {selectedCustomer.video_minutes ?? 0}m
                      </strong>
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
          )}
        </ModalBody>
      </Modal>
    </>
  );
}
